import { cutDownCommitSlug } from './panda-application-config-utils'

export enum AppName {
  Bcr = 'pankkitunnukset',
  CorpKyc = 'yritysasiakas',
  CreditCard = 'hakemukset',
  DigiLa = 'lasten',
  DigiLi = 'digili',
  Kyc = 'asiakastiedot',
  SecLoans = 'lainat',
  SelfService = 'asiointi',
  BankAccounts = 'tilit',
}

export enum AppId {
  Bcr = 'bcr',
  CorpKyc = 'corp-kyc',
  CreditCard = 'credit-card',
  DigiLa = 'digila',
  DigiLi = 'digili',
  Kyc = 'kyc',
  SecLoans = 'sec-loans',
  SelfService = 'self-service',
  BankAccounts = 'bank-accounts',
}

type StackAppConfig = {
  /** Used as a resource ID prefix */
  resourcePrefix: string
  /** App name used in resource description(s) */
  screenName: string
}

export enum JobType {
  deployUi = 'deploy-ui',
  deployBff = 'deploy-bff',
  buildUi = 'build-ui',
  buildBff = 'build-bff',
  unittestUi = 'unittest-ui',
  unittestBff = 'unittest-bff',
  e2e = 'e2e',
}

export enum Environment {
  Production = 'production',
  Staging = 'staging',
  Test = 'test',
  Development = 'development',
  Local = 'local',
}

export interface UiEnvironmentVariables {
  VITE_USERCENTRICS_APP_ID?: string
  VITE_USERCENTRICS_SNOWPLOW_SERVICE_ID?: string
  VITE_FILE_UPLOAD_BUCKET_CONNECT_SOURCE?: string
}

export enum TestRunner {
  HighCpuDocker = 'highcpu-docker',
  OnDemandDocker = 'on-demand-docker',
}

type PipelineApp = {
  snowplowAppIdPrefix: string
  uiEnvironmentVariables?: { default?: UiEnvironmentVariables } & Partial<Record<Environment, UiEnvironmentVariables>>
  useRealIntegrationsE2e?: boolean // - whether to include e2e tests that use real integrations
  shouldRunE2eInTest?: boolean
  shouldRunE2eInStaging?: boolean
  updateAadAuthTokens?: boolean // - whether to update aad auth tokens
  ciJobs?: JobType[]
  shard: { shardIndex: string; shardTotal: string }
  testRunner: TestRunner
}

export interface ApplicationConfig {
  id: AppId
  urlBaseName: AppName
  pipeline: PipelineApp
  stack: StackAppConfig
}

export const applicationConfigs: Record<AppId, ApplicationConfig> = {
  [AppId.CreditCard]: {
    id: AppId.CreditCard,
    urlBaseName: AppName.CreditCard,
    pipeline: {
      snowplowAppIdPrefix: 'bank-hakemukset',
      shard: { shardIndex: '[1]', shardTotal: '1' },
      testRunner: TestRunner.OnDemandDocker,
    },
    stack: {
      resourcePrefix: 'CreditCard',
      screenName: 'Credit Card',
    },
  },
  [AppId.DigiLi]: {
    id: AppId.DigiLi,
    urlBaseName: AppName.DigiLi,
    pipeline: {
      updateAadAuthTokens: true,
      shouldRunE2eInTest: true,
      shouldRunE2eInStaging: true,
      useRealIntegrationsE2e: true,
      snowplowAppIdPrefix: 'digili',
      uiEnvironmentVariables: {
        default: {
          VITE_USERCENTRICS_APP_ID: 'od4iU5RUI',
          VITE_USERCENTRICS_SNOWPLOW_SERVICE_ID: 'IHHxYH8BApY0yf',
        },
        production: {
          VITE_USERCENTRICS_APP_ID: 'Yv591VkI5',
          VITE_USERCENTRICS_SNOWPLOW_SERVICE_ID: 't_UUeh1Vw',
        },
      },
      shard: { shardIndex: '[1]', shardTotal: '1' },
      testRunner: TestRunner.OnDemandDocker,
    },
    stack: {
      resourcePrefix: 'DigiLi',
      screenName: 'DigiLi',
    },
  },
  [AppId.DigiLa]: {
    id: AppId.DigiLa,
    urlBaseName: AppName.DigiLa,
    pipeline: {
      updateAadAuthTokens: true,
      shouldRunE2eInTest: true,
      shouldRunE2eInStaging: true,
      snowplowAppIdPrefix: 'bank-digila',
      shard: { shardIndex: '[1]', shardTotal: '1' },
      testRunner: TestRunner.OnDemandDocker,
    },
    stack: {
      resourcePrefix: 'DigiLa',
      screenName: 'DigiLa',
    },
  },
  [AppId.Kyc]: {
    id: AppId.Kyc,
    urlBaseName: AppName.Kyc,
    pipeline: {
      snowplowAppIdPrefix: 'bank-kyc',
      shard: { shardIndex: '[1]', shardTotal: '1' },
      testRunner: TestRunner.OnDemandDocker,
    },
    stack: {
      resourcePrefix: 'Kyc',
      screenName: 'KYC',
    },
  },
  [AppId.SecLoans]: {
    id: AppId.SecLoans,
    urlBaseName: AppName.SecLoans,
    pipeline: {
      snowplowAppIdPrefix: 'bank-sec-loans',
      shard: { shardIndex: '[1,2,3,4]', shardTotal: '4' },
      testRunner: TestRunner.HighCpuDocker,
      uiEnvironmentVariables: {
        test: {
          VITE_FILE_UPLOAD_BUCKET_CONNECT_SOURCE: 'https://sec-loans-test-file-uploads.s3.eu-north-1.amazonaws.com',
        },
        staging: {
          VITE_FILE_UPLOAD_BUCKET_CONNECT_SOURCE: 'https://sec-loans-staging-file-uploads.s3.eu-north-1.amazonaws.com',
        },
        development: {
          VITE_FILE_UPLOAD_BUCKET_CONNECT_SOURCE: `https://${cutDownCommitSlug}-sec-loans-file-uploads.s3.eu-north-1.amazonaws.com`,
        },
        production: {
          VITE_FILE_UPLOAD_BUCKET_CONNECT_SOURCE: 'https://sec-loans-prod-file-uploads.s3.eu-north-1.amazonaws.com',
        },
      },
    },
    stack: {
      resourcePrefix: 'SecLoans',
      screenName: 'Secured Loans',
    },
  },
  [AppId.Bcr]: {
    id: AppId.Bcr,
    urlBaseName: AppName.Bcr,
    pipeline: {
      snowplowAppIdPrefix: 'bank-bcr',
      shouldRunE2eInTest: true,
      shouldRunE2eInStaging: true,
      useRealIntegrationsE2e: true,
      shard: { shardIndex: '[1]', shardTotal: '1' },
      testRunner: TestRunner.OnDemandDocker,
    },
    stack: {
      resourcePrefix: 'Bcr',
      screenName: 'Bank Credentials Recovery',
    },
  },
  [AppId.CorpKyc]: {
    id: AppId.CorpKyc,
    urlBaseName: AppName.CorpKyc,
    pipeline: {
      snowplowAppIdPrefix: 'bank-corp-kyc',
      uiEnvironmentVariables: {
        test: {
          VITE_FILE_UPLOAD_BUCKET_CONNECT_SOURCE: 'https://corp-kyc-test-file-uploads.s3.eu-north-1.amazonaws.com',
        },
        staging: {
          VITE_FILE_UPLOAD_BUCKET_CONNECT_SOURCE: 'https://corp-kyc-staging-file-uploads.s3.eu-north-1.amazonaws.com',
        },
        development: {
          VITE_FILE_UPLOAD_BUCKET_CONNECT_SOURCE: `https://${cutDownCommitSlug}-corp-kyc-file-uploads.s3.eu-north-1.amazonaws.com`,
        },
        production: {
          VITE_FILE_UPLOAD_BUCKET_CONNECT_SOURCE: 'https://corp-kyc-prod-file-uploads.s3.eu-north-1.amazonaws.com',
        },
      },
      shard: { shardIndex: '[1,2]', shardTotal: '2' },
      testRunner: TestRunner.OnDemandDocker,
    },
    stack: {
      resourcePrefix: 'CorpKyc',
      screenName: 'Corporate KYC',
    },
  },
  [AppId.SelfService]: {
    id: AppId.SelfService,
    urlBaseName: AppName.SelfService,
    pipeline: {
      snowplowAppIdPrefix: 'bank-self-service',
      shard: { shardIndex: '[1]', shardTotal: '1' },
      testRunner: TestRunner.OnDemandDocker,
      uiEnvironmentVariables: {
        test: {
          VITE_FILE_UPLOAD_BUCKET_CONNECT_SOURCE: 'https://self-service-test-file-uploads.s3.eu-north-1.amazonaws.com',
        },
        staging: {
          VITE_FILE_UPLOAD_BUCKET_CONNECT_SOURCE:
            'https://self-service-staging-file-uploads.s3.eu-north-1.amazonaws.com',
        },
        development: {
          VITE_FILE_UPLOAD_BUCKET_CONNECT_SOURCE: `https://${cutDownCommitSlug}-self-service-file-uploads.s3.eu-north-1.amazonaws.com`,
        },
        production: {
          VITE_FILE_UPLOAD_BUCKET_CONNECT_SOURCE: 'https://self-service-prod-file-uploads.s3.eu-north-1.amazonaws.com',
        },
      },
    },
    stack: {
      resourcePrefix: 'SelfService',
      screenName: 'Self Service',
    },
  },
  [AppId.BankAccounts]: {
    id: AppId.BankAccounts,
    urlBaseName: AppName.BankAccounts,
    pipeline: {
      snowplowAppIdPrefix: 'bank-bank-accounts',
      shouldRunE2eInTest: false,
      shouldRunE2eInStaging: false,
      useRealIntegrationsE2e: false,
      shard: { shardIndex: '[1]', shardTotal: '1' },
      testRunner: TestRunner.OnDemandDocker,
    },
    stack: {
      resourcePrefix: 'BankAccounts',
      screenName: 'Bank Accounts',
    },
  },
}

export const loanApplicationsDynamoTableName: Record<Environment, string> = {
  [Environment.Production]: 'sec-loans-applications-prod',
  [Environment.Staging]: 'sec-loans-applications-staging',
  [Environment.Test]: 'sec-loans-applications-test',
  [Environment.Development]: 'sec-loans-applications-dev',
  [Environment.Local]: 'sec-loans-applications-dev',
}

export const ifpDynamoTableName: Record<Environment, string> = {
  [Environment.Production]: 'sec-loans-ifp-prod',
  [Environment.Staging]: 'sec-loans-ifp-staging',
  [Environment.Test]: 'sec-loans-ifp-test',
  [Environment.Development]: 'sec-loans-ifp-dev',
  [Environment.Local]: 'sec-loans-ifp-dev',
}

export const loanOffersDynamoTableName: Record<Environment, string> = {
  [Environment.Production]: 'sec-loans-offers-prod',
  [Environment.Staging]: 'sec-loans-offers-staging',
  [Environment.Test]: 'sec-loans-offers-test',
  [Environment.Development]: 'sec-loans-offers-dev',
  [Environment.Local]: 'sec-loans-offers-dev',
}
